import React from "react";
import { cn } from "../../../lib/utils";
import QuestionFrame from "./QuestionFrame";

/**
 * Discrete rating slider with tick labels under the track.
 *
 * @param {Object} props
 * @param {object} props.question
 * @param {number} [props.value]
 * @param {function(number): void} props.onChange
 * @param {string} [props.error]
 * @param {boolean} [props.readOnly]
 * @returns {JSX.Element}
 */
export default function RatingQuestion({ question, value, onChange, error, readOnly, showRequired }) {
  const fieldId = `survey-q-${question.id}`;
  const min = Number(question.min);
  const max = Number(question.max);
  const answered = typeof value === "number";
  const current = answered ? value : min;
  const span = max - min;
  const fillPct = answered && span > 0 ? ((current - min) / span) * 100 : 0;
  const ticks = [];
  for (let n = min; n <= max; n += 1) {
    ticks.push(n);
  }

  return (
    <QuestionFrame question={question} error={error} showRequired={showRequired}>
      <div className="survey-rating flex flex-col gap-2" style={{ "--survey-rating-fill": `${fillPct}%` }}>
        <input
          id={fieldId}
          type="range"
          min={min}
          max={max}
          step={1}
          value={current}
          disabled={readOnly}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={answered ? current : undefined}
          onChange={(e) => onChange(Number(e.target.value))}
          onPointerDown={() => {
            if (!readOnly && !answered) onChange(current);
          }}
          className="survey-rating-input disabled:cursor-not-allowed disabled:opacity-60"
          style={{ "--survey-rating-fill": `${fillPct}%` }}
        />
        <div className="survey-rating-ticks relative h-4">
          {ticks.map((tick, index) => {
            const left = ticks.length === 1 ? 0 : (index / (ticks.length - 1)) * 100;
            return (
              <span
                key={tick}
                className={cn(
                  "absolute top-0 -translate-x-1/2 text-xs tabular-nums text-text-muted",
                  answered && tick === current && "font-medium text-text-primary"
                )}
                style={{ left: `${left}%` }}
              >
                {tick}
              </span>
            );
          })}
        </div>
      </div>
    </QuestionFrame>
  );
}
